
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MdAdd, MdDelete, MdSave, MdArrowBack } from 'react-icons/md';
import Swal from 'sweetalert2';
import Select from 'react-select';
import axios from 'axios';
import { GET_ALL_PRODUCTS_API, GET_PO_DETAILS_BY_PO_ID_API, GET_COMPLETED_PO_DETAILS_BY_PO_ID_API, UPDATE_PO_API } from '../config/API';
import Loader from '../components/common/Loader';
import PaginatedSelect from '../components/common/PaginatedSelect';
// import './AddGenerateRequest.css';
// import './Dashboard.css';

const POEntry = () => {
    const { poId, id } = useParams();
    const routeId = poId || id;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // State to hold the full PO structure for editing
    const [poData, setPoData] = useState({
        projectId: '',
        date: '',
        tillDate: '',
        particular: '',
        vendors: []
    });

    // Products are handled by PaginatedSelect

    useEffect(() => {
        const fetchData = async () => {
            if (!routeId) return;

            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const headers = { Authorization: `Bearer ${token}` };

                // 1. Fetch Products logic is now handled by PaginatedSelect

                // 2. Try Fetching from standard PO details API first
                let poResponse;
                try {
                    poResponse = await axios.get(GET_PO_DETAILS_BY_PO_ID_API(routeId), { headers });
                } catch (e) {
                    poResponse = { data: { status: false } };
                }

                // 3. Fallback to Completed PO details if the first one fails or returns no data
                if (!poResponse.data.status || !poResponse.data.data) {
                    console.log("Standard API returned no data, trying Completed PO details API...");
                    try {
                        const completedResponse = await axios.get(GET_COMPLETED_PO_DETAILS_BY_PO_ID_API(routeId), { headers });
                        if (completedResponse.data.status) {
                            poResponse = completedResponse;
                        }
                    } catch (e) {
                        console.error("Both PO detail APIs failed");
                    }
                }

                if (poResponse.data.status) {
                    const data = poResponse.data.data;
                    const requestData = data.request || data.po || data;

                    // Robust Vendor Mapping
                    const rawVendors = requestData.PurchaseOrderVendors ||
                        requestData.RequestVendors ||
                        requestData.Vendors ||
                        (requestData.RequestVendor ? [requestData.RequestVendor] : []) ||
                        (requestData.PurchaseOrderVendor ? [requestData.PurchaseOrderVendor] : []);

                    let formattedVendors = rawVendors.map(pov => {
                        const vendorObj = pov.Vendor || pov.RequestVendor?.Vendor || pov;
                        const itemsList = pov.PurchaseOrderItems ||
                            pov.RequestItems ||
                            pov.Items ||
                            pov.PurchaseOrderItems ||
                            pov.VendorsRequests || [];

                        return {
                            id: pov.id,
                            reqVendorId: pov.reqVendorId || pov.RequestVendor?.id || (requestData.RequestVendor?.id === pov.id ? requestData.RequestVendor.id : pov.id),
                            vendorId: vendorObj.id || pov.vendorId,
                            vendorName: vendorObj.vendorName || vendorObj.companyName || 'Unknown Vendor',
                            items: itemsList.map(item => ({
                                id: item.id,
                                reqItemId: item.reqItemId,
                                productId: item.productId,
                                productName: item.Product?.productName || item.productDescription,
                                size: item.size || '',
                                qty: item.qty || 0,
                                rate: item.rate || 0,
                                unit: item.Unit?.unitName || item.unit || item.Product?.Unit?.unitName || 'Nos',
                                unitId: item.unitId || item.Unit?.id || item.Product?.Unit?.id,
                                total: parseFloat(item.qty || 0) * parseFloat(item.rate || 0)
                            }))
                        };
                    });

                    // Fallback for items if not found inside vendors
                    if (formattedVendors.length > 0 && formattedVendors.every(v => v.items.length === 0)) {
                        const rootItems = requestData.PurchaseOrderItems ||
                            requestData.RequestItems ||
                            requestData.Items || [];

                        if (rootItems.length > 0) {
                            formattedVendors[0].items = rootItems.map(item => ({
                                id: item.id,
                                reqItemId: item.reqItemId,
                                productId: item.productId,
                                productName: item.Product?.productName || item.productDescription,
                                size: item.size || '',
                                qty: item.qty || 0,
                                rate: item.rate || 0,
                                unit: item.Unit?.unitName || item.unit || item.Product?.Unit?.unitName || 'Nos',
                                unitId: item.unitId || item.Unit?.id || item.Product?.Unit?.id,
                                total: parseFloat(item.qty || 0) * parseFloat(item.rate || 0)
                            }));
                        }
                    }

                    // If still no vendors, try matching a single vendor from root
                    if (formattedVendors.length === 0 && (requestData.vendorId || requestData.Vendor)) {
                        const vObj = requestData.Vendor || {};
                        formattedVendors = [{
                            id: 'root-vendor',
                            vendorId: requestData.vendorId || vObj.id,
                            vendorName: vObj.vendorName || vObj.companyName || 'Main Vendor',
                            items: []
                        }];
                    }

                    setPoData({
                        id: requestData.id,
                        poNumber: requestData.poNumber || `PO-${requestData.id}`,
                        projectId: requestData.projectId,
                        date: requestData.date ? requestData.date.split('T')[0] : new Date().toISOString().split('T')[0],
                        tillDate: requestData.validTill ? requestData.validTill.split('T')[0] : (requestData.tillDate ? requestData.tillDate.split('T')[0] : ''),
                        particular: requestData.particular || '',
                        vendors: formattedVendors,
                        grandTotal: requestData.grandTotal || 0
                    });
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                Swal.fire('Error', 'Failed to load PO details.', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [routeId]);

    // Handle Header Field Changes
    const handleHeaderChange = (field, value) => {
        setPoData(prev => ({ ...prev, [field]: value }));
    };

    // Handle Item Changes (Qty/Rate)
    const handleItemChange = (vendorIndex, itemIndex, field, value) => {
        const newVendors = [...poData.vendors];

        // Update the field
        newVendors[vendorIndex].items[itemIndex][field] = value;

        // Recalculate Item Total (for display)
        const qty = parseFloat(newVendors[vendorIndex].items[itemIndex].qty) || 0;
        const rate = parseFloat(newVendors[vendorIndex].items[itemIndex].rate) || 0;
        newVendors[vendorIndex].items[itemIndex].total = qty * rate;

        setPoData(prev => ({ ...prev, vendors: newVendors }));
    };

    // Add Item to Vendor
    const handleAddItemToVendor = (vendorIndex, selectedProduct) => {
        if (!selectedProduct) return;

        const newVendors = [...poData.vendors];

        const newItem = {
            id: `temp-${Date.now()}`, // Temporary ID for React keys only
            isNew: true, // Flag as a new item to omit ID in payload
            reqItemId: null, // Set to null for ad-hoc items to avoid foreign key constraint errors (References RequestItems)
            productId: selectedProduct.value,
            productName: selectedProduct.label,
            qty: 1,
            rate: parseFloat(selectedProduct.rate) || 0,
            unit: selectedProduct.unit || 'Nos',
            unitId: selectedProduct.unitId,
            size: '',
            total: parseFloat(selectedProduct.rate) || 0
        };

        newVendors[vendorIndex].items.push(newItem);
        setPoData(prev => ({ ...prev, vendors: newVendors }));
    };

    // Remove Item from Vendor
    const handleRemoveItemFromVendor = (vendorIndex, itemIndex) => {
        const newVendors = [...poData.vendors];
        newVendors[vendorIndex].items.splice(itemIndex, 1);
        setPoData(prev => ({ ...prev, vendors: newVendors }));
    };

    // Calculate Grand Total dynamically
    const calculateGrandTotal = () => {
        let total = 0;
        poData.vendors.forEach(v => {
            v.items.forEach(item => {
                total += (parseFloat(item.qty) || 0) * (parseFloat(item.rate) || 0);
            });
        });
        return total;
    };

    // Handle Submit
    const handleSubmit = async () => {
        // Validation
        // if (!poData.projectId || !poData.date || !poData.tillDate) {
        //     Swal.fire('Error', 'Please fill in all required fields (Date, Valid Till).', 'warning');
        //     return;
        // }

        const result = await Swal.fire({
            title: 'Update PO?',
            text: `You are about to update changes for PO #${poData.poNumber}.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update',
            confirmButtonColor: '#10b981'
        });

        if (result.isConfirmed) {
            setSubmitting(true);
            try {
                // Construct Payload
                const payload = {
                    date: poData.date,
                    tillDate: poData.tillDate,
                    particular: poData.particular,

                    // ✅ MATCH REQUESTED JSON PAYLOAD STRUCTURE
                    items: poData.vendors.flatMap(v =>
                        v.items.map(item => {
                            const itemPayload = {
                                reqItemId: item.reqItemId,
                                productId: item.productId,
                                size: item.size || "",
                                qty: parseFloat(item.qty),
                                rate: parseFloat(item.rate),
                                unitId: item.unitId
                            };

                            // Only add 'id' if it's an existing database record (not marked as isNew)
                            if (item.id && !item.isNew) {
                                itemPayload.id = item.id;
                            }

                            return itemPayload;
                        })
                    )
                };

                const token = localStorage.getItem('accessToken');
                const response = await axios.put(UPDATE_PO_API(routeId), payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire('Updated!', 'Purchase Order has been updated successfully.', 'success')
                        .then(() => {
                            navigate('/po-management'); // Optional: go back
                        });
                } else {
                    Swal.fire('Error', response.data.message || 'Failed to update PO.', 'error');
                }

            } catch (error) {
                console.error('Error updating PO:', error);
                Swal.fire('Error', 'Failed to update PO.', 'error');
            } finally {
                setSubmitting(false);
            }
        }
    };

    if (loading) return <Loader text="Loading PO Details..." />;
    if (!poData || !poData.poNumber) return <div className="p-8">PO Not Found or Error Loading Data</div>;

    const currentGrandTotal = calculateGrandTotal();

    return (
        <div className="add-po-page">
            <div className="page-header">
                <button className="back-btn" onClick={() => navigate('/po-management')}>
                    <MdArrowBack />
                </button>
                <h2>Purchase Order: {poData.poNumber}</h2>
            </div>

            <div className="project-form">

                <div className="form-section">
                    <h3>General Information</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label>PO Number</label>
                            <input type="text" value={poData.poNumber} disabled className="form-input readonly-field" />
                        </div>
                        <div className="form-group">
                            <label>Particular</label>
                            <input
                                type="text"
                                value={poData.particular}
                                onChange={(e) => handleHeaderChange('particular', e.target.value)}
                                className="form-input"
                                placeholder="Enter Particular"
                            />
                        </div>
                        <div className="form-group">
                            <label>Date</label>
                            <input
                                type="date"
                                value={poData.date}
                                onChange={(e) => handleHeaderChange('date', e.target.value)}
                                className="form-input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Valid Till</label>
                            <input
                                type="date"
                                value={poData.tillDate}
                                onChange={(e) => handleHeaderChange('tillDate', e.target.value)}
                                className="form-input"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3>PO Items</h3>
                    </div>

                    {poData.vendors.map((vendor, vIndex) => (
                        <div key={vendor.reqVendorId} className="vendor-section" style={{ marginBottom: '2rem', border: '1px solid #e5e7eb', padding: '1rem', borderRadius: '8px' }}>
                            <h4 style={{ marginBottom: '1rem', color: '#4f46e5', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
                                Vendor: {vendor.vendorName}
                            </h4>

                            <div style={{ marginBottom: '1rem' }}>
                                <PaginatedSelect
                                    apiUrl={GET_ALL_PRODUCTS_API}
                                    mapResponse={(p) => ({
                                        value: p.id || p._id,
                                        label: p.productName,
                                        rate: parseFloat(p.defaultRate) || 0,
                                        unit: p.Unit?.unitName || p.unitName || '',
                                        unitId: p.Unit?.id || p.unitId || ''
                                    })}
                                    onChange={(selected) => handleAddItemToVendor(vIndex, selected)}
                                    placeholder="Search & Select Product to Add..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    value={null}
                                    isClearable
                                />
                            </div>

                            <div className="items-table-container">
                                <table className="items-table">
                                    <thead className="thead-po">
                                        <tr>
                                            <th>Item </th>
                                            <th style={{ width: '100px' }}>Qty</th>
                                            <th style={{ width: '100px' }}>Unit</th>
                                            <th style={{ width: '120px' }}>Rate</th>
                                            <th style={{ width: '120px' }}>Total</th>
                                            <th style={{ width: '80px', textAlign: 'center' }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vendor.items.map((item, iIndex) => (
                                            <tr key={item.id || iIndex}>
                                                <td>{item.productName}</td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={item.qty}
                                                        onChange={(e) => handleItemChange(vIndex, iIndex, 'qty', e.target.value)}
                                                        min="1"
                                                    />
                                                </td>
                                                <td>{item.unit}</td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        value={item.rate}
                                                        onChange={(e) => handleItemChange(vIndex, iIndex, 'rate', e.target.value)}
                                                        min="0"
                                                    />
                                                </td>
                                                <td>
                                                    <div className="readonly-cell" style={{ fontWeight: 'bold' }}>
                                                        {parseFloat(item.total).toLocaleString()}
                                                    </div>
                                                </td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <button
                                                        className="delete-btn"
                                                        onClick={() => handleRemoveItemFromVendor(vIndex, iIndex)}
                                                        title="Remove Item"
                                                        style={{
                                                            background: '#fee2e2',
                                                            color: '#ef4444',
                                                            border: 'none',
                                                            padding: '0.4rem',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        <MdDelete size={20} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}

                    <div style={{ marginTop: '1rem', textAlign: 'right', fontSize: '1.2rem', fontWeight: 'bold' }}>
                        Grand Total: ₹{currentGrandTotal.toLocaleString()}
                    </div>
                </div>

                <div className="form-actions" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <button type="button" className="cancel-btn" onClick={() => navigate('/po-management')} style={{ padding: '0.75rem 1.5rem', border: '1px solid #e5e7eb', background: 'white', color: '#374151', borderRadius: '8px', cursor: 'pointer', fontWeight: '500' }}>Cancel</button>
                    <button type="button" className="submit-btn" onClick={handleSubmit} disabled={submitting} style={{ padding: '0.75rem 1.5rem', background: '#6366f1', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {submitting ? 'Updating...' : (
                            <>
                                <MdSave size={20} /> Save PO
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default POEntry;
